import { Cradle, dayjs, Liquidation, PaginatedEntity } from '@solo/app-core';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { translate } from '@/app/i18n';
import { Button, Flex, Text } from '@/app/ui/atoms';
import Colors from '@/app/styles/Colors';
import { Icon } from '@/app/ui/molecules';
import { useCradle } from '@/app/contexts';

const StatementList = ({ statements }: { statements: PaginatedEntity<Liquidation> }) => {
  const [requestClicked, setRequestClicked] = useState(false);
  const { liquidationsBloc } = useCradle<Cradle>();

  const showPeriod = (statement: Liquidation) =>
    `${dayjs(statement.dateFrom).format('DD/MM/YYYY')} - ${dayjs(statement.dateTo).format(
      'DD/MM/YYYY',
    )}`;

  const requestLiquidationCsvReport = async (liquidationId: string) => {
    try {
      await liquidationsBloc.generateLiquidationFile(liquidationId);
      setRequestClicked(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error('Error requesting CSV report');
    }
  };

  const downloadStatement = async (statement: Liquidation) => {
    if (!statement.csvReportId) {
      toast.error('No CSV report found');
      return;
    }

    try {
      const period = showPeriod(statement);
      const report = await liquidationsBloc.downloadLiquidationFile(statement.csvReportId);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(report);
      link.setAttribute('download', statement.csvReport?.name || `Liquidation-${period}.csv`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      toast.error('Error downloading CSV report');
    }
  };

  function requestOrDownloadStatement(statement: Liquidation) {
    if (!statement.csvReportRequestedAt) {
      requestLiquidationCsvReport(statement.id);
      return;
    }
    // statement.csvReport is a file
    if (!statement.csvReport) {
      toast.error('No CSV report found');
      return;
    }
    downloadStatement(statement);
  }

  return (
    <Flex.Container className="table" flexDirection="column">
      <Flex.Container gap="16px" className="thead">
        <Flex.Item flex={3} className="th" style={{ paddingLeft: '1rem' }}>
          <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
            {translate('EARNINGS.PERIOD')}
          </Text.Paragraph>
        </Flex.Item>
        <Flex.Item flex={3} className="th">
          <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
            {translate('WORKS.STATUS')}
          </Text.Paragraph>
        </Flex.Item>
        <Flex.Item flex={3} className="th">
          <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
            {translate('WORKS.EXPEDITION_DATE')}
          </Text.Paragraph>
        </Flex.Item>
        <Flex.Item flex={3} className="th">
          <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
            {translate('EARNINGS.AMOUNT')}
          </Text.Paragraph>
        </Flex.Item>
        <Flex.Item flex={2} className="th">
          <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
            {translate('ACTIONS.ACTIONS')}
          </Text.Paragraph>
        </Flex.Item>
      </Flex.Container>
      <Flex.Container className="tbody" flexDirection="column">
        {statements.data.map((statement) => (
          <Flex.Container
            key={statement.id}
            className="tr"
            alignItems="center"
            gap="16px"
            style={{ padding: '0.5rem' }}
          >
            <Flex.Item flex={3} className="td" style={{ paddingLeft: '1rem' }}>
              <Text.Paragraph fontWeight={700}>{showPeriod(statement)}</Text.Paragraph>
            </Flex.Item>
            <Flex.Item flex={3} className="td">
              {translate(`WORKS.STATEMENTS_STATUS.${statement.clientStatement.status}`)}
            </Flex.Item>
            <Flex.Item flex={3} className="td">
              {statement.clientStatement.expeditionDate || '-'}
            </Flex.Item>
            <Flex.Item flex={3} className="td">
              {statement.clientStatement.totalAmount.toFixed(2)} €
            </Flex.Item>
            <Flex.Item flex={2} className="td">
              {requestClicked || (statement.csvReportRequestedAt && !statement.csvReportId) ? (
                <Text.Paragraph fontWeight={700} style={{ padding: 0, color: Colors.grey }}>
                  {translate('DOWNLOAD.GENERATING')}
                </Text.Paragraph>
              ) : (
                <Button $size="md" $transparent style={{ padding: 0 }} className="clickable">
                  <Flex.Container
                    gap="0.5rem"
                    alignItems="center"
                    onClick={() => requestOrDownloadStatement(statement)}
                  >
                    {statement.csvReportId ? <Icon.DownloadIcon /> : null}
                    <Text.Paragraph fontWeight={700} style={{ padding: 0, color: Colors.primary }}>
                      {statement.csvReportRequestedAt &&
                        statement.csvReportId &&
                        translate('DOWNLOAD.DOWNLOAD')}
                      {!statement.csvReportRequestedAt &&
                        !statement.csvReportId &&
                        translate('DOWNLOAD.REQUEST')}
                    </Text.Paragraph>
                  </Flex.Container>
                </Button>
              )}
            </Flex.Item>
          </Flex.Container>
        ))}
      </Flex.Container>
    </Flex.Container>
  );
};

export default StatementList;
